import {
  useLoanQuotes,
  useLenders,
  useApplicationDetail,
  useQuoteStatusUtils,
  useRequestStatusUtils,
} from '@axo/shared/data-access/hooks';
import {
  lender,
  loan_application,
  loan_quote,
  loan_request,
} from '@axo/shared/data-access/types';

export interface IQuoteWithSubQuote extends loan_quote.BankQuote {
  LoanQuoteID: string;
  IsSubLoanQuote?: boolean;
}

export interface IQuoteDetail extends IQuoteWithSubQuote {
  Lender: lender.Lender;
}

export interface IBankPanelDataDetail {
  name: string;
  data: IQuoteDetail[];
  value: string;
}

export type IBankPanelKey =
  | 'offers'
  | 'declined'
  | 'notRegistered'
  | 'pending'
  | 'closed'
  | 'allBanks'
  | 'allRegistrations';

type IBankPanelData = Record<IBankPanelKey, IBankPanelDataDetail>;

export function useBankPanelData(applicationID: string) {
  const loanQuoteQuery = useLoanQuotes(applicationID);
  const application = useApplicationDetail(applicationID);
  const lenders = useLenders(application?.data?.MarketCountry ?? '');
  const { isInRequestStatusGroup } = useRequestStatusUtils();
  const { isInQuoteStatusGroup } = useQuoteStatusUtils();

  function placeholderLoanQuote(
    ID: string,
    lender: lender.Lender,
    application: loan_application.LoanApplication,
  ): IQuoteDetail {
    return {
      ID: ID,
      CreatedAt: '',
      UpdatedAt: '',
      Product: application.Product,
      // @ts-expect-error type mismatch
      Status: undefined,
      LoanRequestID: '',
      LenderID: lender.ID,
      EffectiveRate: null,
      NominalRate: null,
      MonthlyPayment: 0,
      StartupFee: 0,
      AdministrationFee: 0,
      Amount: 0,
      Duration: 0,
      TotalPayment: 0,
      ApplicationID: application.ID,
      IsPlaceholderQuote: true,
      LoanRequest: {
        LoanRequestID: '',
        CreatedAt: '',
        CreatedBy: '',
        Status:
          loan_request.LoanRequestStatus
            .LoanRequestStatusApplicationCompletedNotRegistered,
        LenderStatus: '',
      },
      Lender: lender,
    };
  }

  const isLoading =
    loanQuoteQuery.isLoading || application.isLoading || lenders.isLoading;

  const bankPanelData: IBankPanelData = {
    offers: {
      name: 'Offers',
      value: '10',
      data: [],
    },
    declined: {
      name: 'Declined',
      value: '10',
      data: [],
    },
    notRegistered: {
      name: 'NotRegistered',
      value: '10',
      data: [],
    },
    pending: {
      name: 'Pending',
      value: '10',
      data: [],
    },
    closed: {
      name: 'Closed',
      value: '10',
      data: [],
    },
    allBanks: {
      name: 'All banks',
      value: '10',
      data: [],
    },
    allRegistrations: {
      name: 'All registrations',
      value: '10',
      data: [],
    },
  };

  // Closed
  if (application.data && lenders.data)
    bankPanelData.closed.data = lenders.data
      ?.filter((lender) => !lender.Active)
      .map((lender, index) =>
        placeholderLoanQuote(
          index.toString() + 'closed',
          lender,
          application.data,
        ),
      );

  const loanQuotesAndSubloanQuotes: IQuoteWithSubQuote[] | undefined =
    loanQuoteQuery?.data?.flatMap((quote) => {
      if (quote.SubLoanQuote) {
        const subQuote = {
          ...quote,
          ...quote.SubLoanQuote,
          LoanQuoteID: quote.ID,
          IsSubLoanQuote: true,
        };
        return [{ ...quote, LoanQuoteID: quote.ID }, subQuote];
      }
      return { ...quote, LoanQuoteID: quote.ID };
    });

  loanQuotesAndSubloanQuotes?.forEach((quote) => {
    const lender = lenders?.data?.find(
      (lender) => lender.ID === quote.LenderID,
    );
    if (!lender) return;

    const quoteDetail = { ...quote, Lender: lender };

    bankPanelData.allBanks.data.push(quoteDetail);

    // All registrations
    bankPanelData.allRegistrations.data.push(quoteDetail);

    // Offers
    if (
      isInQuoteStatusGroup(quote.Status, loan_quote.QuoteStatusGroup.Approved)
    ) {
      bankPanelData.offers.data.push(quoteDetail);
      return;
    }
    // Declined
    if (
      isInQuoteStatusGroup(
        quote.Status,
        loan_quote.QuoteStatusGroup.Cancelled,
      ) ||
      isInQuoteStatusGroup(quote.Status, loan_quote.QuoteStatusGroup.Rejected)
    ) {
      bankPanelData.declined.data.push(quoteDetail);
      return;
    }
    // Pending
    if (
      isInRequestStatusGroup(
        quote?.LoanRequest?.Status,
        loan_request.RequestStatusGroup.PendingRegistration,
      )
    ) {
      bankPanelData.pending.data.push(quoteDetail);
      return;
    }
    if (
      isInRequestStatusGroup(
        quote?.LoanRequest?.Status,
        loan_request.RequestStatusGroup.NotRegistered,
      )
    ) {
      bankPanelData.notRegistered.data.push(quoteDetail);
      return;
    }
  });

  // All banks
  if (application.data && lenders.data) {
    lenders.data.forEach((lender, index) => {
      const bankNotAdded = !bankPanelData.allBanks.data.find(
        (allBanksQuote) => lender.ID === allBanksQuote.LenderID,
      );
      if (bankNotAdded)
        bankPanelData.allBanks.data.push(
          placeholderLoanQuote(index.toString(), lender, application.data),
        );
    });
  }

  // NotRegistered
  bankPanelData.notRegistered.data = bankPanelData.allBanks.data.filter(
    (quote) =>
      isInRequestStatusGroup(
        quote.LoanRequest.Status,
        loan_request.RequestStatusGroup.NotRegistered,
      ),
  );

  function getTier(lender: lender.Lender): number {
    return (
      lender.BrokerConfiguration?.find(
        (config) => config.Broker === application.data?.Meta?.Broker,
      )?.Tier ?? 1
    );
  }

  bankPanelData.allBanks.data.sort(
    (a, b) => getTier(a.Lender) - getTier(b.Lender),
  );
  bankPanelData.closed.data.sort(
    (a, b) => getTier(a.Lender) - getTier(b.Lender),
  );
  bankPanelData.declined.data.sort(
    (a, b) => getTier(a.Lender) - getTier(b.Lender),
  );
  bankPanelData.notRegistered.data.sort(
    (a, b) => getTier(a.Lender) - getTier(b.Lender),
  );
  bankPanelData.offers.data.sort(
    (a, b) => getTier(a.Lender) - getTier(b.Lender),
  );
  bankPanelData.pending.data.sort(
    (a, b) => getTier(a.Lender) - getTier(b.Lender),
  );
  bankPanelData.allRegistrations.data.sort(
    (a, b) => getTier(a.Lender) - getTier(b.Lender),
  );

  return { bankPanelData: bankPanelData, isLoading: isLoading };
}
